// Vereinsseite in der Verwaltung: Kopf, Zahlen, Reiter Wettkämpfer / Anmeldungen / Helfer / Vereinsdaten.
// Muster ~/Documents/TDA-Muster/Muster_Vereine_Verwaltung.html, Variante A (Entscheidung 22.10.2026).
// /dashboard/vereine/:id/bearbeiten öffnet direkt die Vereinsdaten im Bearbeitungsmodus (MainDashboard.jsx).
import React, { useState, useEffect, useMemo, useCallback } from 'react';
// Aufgabe 161: Gurtwahl belegt das Skill-Level vor (Verein darf danach ändern)
import { skillAusGurt } from '../../lib/skillAusGurt';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getWettkaempferByVerein, addWettkaempfer } from '../../api/wettkaempferApi';
import WettkampferAuswahlModal from '../../components/registration/WettkampferAuswahlModal';
import '../../styles/VereinDetailPage.css';
import '../../components/dashboard/WettkaempferDashboard.css';
import '../../styles/vereine.css';
import { toast } from '../../components/common/Toast';
import FotoHallenEinwilligung from '../../components/fotos/FotoHallenEinwilligung';
import { GeschuetztesBild } from '../../lib/geschuetztesBild';
import {
  apiJson, istVerein, datum, tageSeit, plzOrt, pruefeVereinsdaten, alterAus, jahrgang, gurtHintergrund, istStorniert,
} from './vereine/vereinHilfen';
import {
  Sym, Siegel, StatusAbzeichen, Fehlt, LeerKarte,
  SperrenDialog, FreigebenDialog, ArchivDialog, LoeschenDialog, BestaetigenDialog,
} from './vereine/VereinTeile';
import { useVereinAktionen } from './vereine/useVereinAktionen';

// Dropdown-Optionen wie in der DB!
const skillLevels = ["Beginner", "Intermediate", "Advanced", "Black Belt"];

const STAFF_ROLLEN = [
  { value: 'hauptkampfrichter', label: 'Hauptkampfrichter', icon: '👨‍⚖️' },
  { value: 'seitenkampfrichter', label: 'Seitenkampfrichter', icon: '⚖️' },
  { value: 'punktrichter', label: 'Punktrichter', icon: '📝' },
  { value: 'zeitnehmer', label: 'Zeitnehmer', icon: '⏱️' },
  { value: 'mattenleiter', label: 'Mattenleiter', icon: '🎯' },
  { value: 'schreiber', label: 'Schreiber', icon: '✍️' },
  { value: 'helfer', label: 'Helfer', icon: '🤝' },
  { value: 'arzt', label: 'Arzt', icon: '👨‍⚕️' },
  { value: 'sanitaeter', label: 'Sanitäter', icon: '🚑' }
];

const QUALIFIKATIONEN = [
  { value: 'keine', label: 'Keine Lizenz', icon: '⚪' },
  { value: 'c_lizenz', label: 'C-Lizenz', icon: '🟢' },
  { value: 'b_lizenz', label: 'B-Lizenz', icon: '🔵' },
  { value: 'a_lizenz', label: 'A-Lizenz', icon: '🟡' },
  { value: 'international', label: 'International', icon: '🔴' }
];

const gurtfarben = [
  "Weiß", "Weiß/Gelb", "Gelb", "Orange", "Grün", "Grün/Blau", "Blau",
  "Lila", "Blau/Rot", "Rot", "Braun", "Schwarz"
];

const MONATE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const REITER_AUS_ADRESSE = { anmeldungen: 'anm', anm: 'anm', helfer: 'helfer', personal: 'helfer', daten: 'daten', wettkaempfer: 'wk', wk: 'wk' };

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

// Preisberechnung für Anmeldungen (nur wenn die gespeicherte Gebühr fehlt)
function calculatePrice(kategorienCount, preisEinzel, preisTeam, rabattZweite, rabattWeitere, isTeam = false) {
  const basisPreis = isTeam ? (preisTeam || 40) : (preisEinzel || 25);
  let gesamt = 0;

  for (let i = 0; i < kategorienCount; i++) {
    if (i === 0) {
      gesamt += basisPreis;
    } else if (i === 1) {
      gesamt += basisPreis * (1 - (rabattZweite || 20) / 100);
    } else {
      gesamt += basisPreis * (1 - (rabattWeitere || 40) / 100);
    }
  }

  return gesamt;
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
}

function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop vdp-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

const leererVerein = (v) => ({
  name: v?.name || '', ansprechpartner: v?.ansprechpartner || '', email: v?.email || '', telefon: v?.telefon || '',
  homepage: v?.homepage || '', strasse: v?.strasse || '', hausnummer: v?.hausnummer || '', plz: v?.plz || '', ort: v?.ort || '',
  logo: v?.logo || null,
});

function Feld({ id, label, wert, onWert, lesen, fehler, klasse = '', typ = 'text', nurLesen = false, tipp }) {
  return (
    <div className={`vv-feld ${klasse}${fehler ? ' vv-ist-fehler' : ''}`}>
      <label htmlFor={`vv-f-${id}`}>{label}</label>
      <input id={`vv-f-${id}`} type={typ} value={wert || ''} readOnly={lesen || nurLesen} tabIndex={lesen ? -1 : undefined}
        aria-invalid={fehler ? 'true' : undefined} aria-describedby={fehler ? `vv-f-${id}-fehl` : undefined}
        onChange={(e) => onWert?.(e.target.value)} />
      {fehler && <span className="vv-fehl" id={`vv-f-${id}-fehl`}><Sym n="achtung" />{fehler}</span>}
      {!fehler && tipp && !lesen && <span className="vv-tipp">{tipp}</span>}
    </div>
  );
}

export default function VereinDetailPage({ vereinId: propVereinId, bearbeiten = false }) {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Use prop vereinId if provided, otherwise fall back to URL param
  const id = propVereinId || paramId;

  const [verein, setVerein] = useState(null);
  const [ladeFehler, setLadeFehler] = useState(null);
  const [wettkaempfer, setWettkaempfer] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalError, setModalError] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [wkGurt, setWkGurt] = useState('');
  const [wkG, setWkG] = useState('');
  const [isEditing, setIsEditing] = useState(!!bearbeiten);
  const [editedVerein, setEditedVerein] = useState(null);
  const [feldFehler, setFeldFehler] = useState({});
  const [speicherFehler, setSpeicherFehler] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWettkaempfer, setEditingWettkaempfer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailWettkaempfer, setDetailWettkaempfer] = useState(null);
  // Gewähltes Foto wartet auf die Antwort „auf Hallenbildschirmen zeigen?“ (21.10.2026)
  const [fotoDatei, setFotoDatei] = useState(null);
  const [menue, setMenue] = useState(false);
  const [dialog, setDialog] = useState(null); // { art, daten }

  // Reiter: ?tab=… oder /bearbeiten
  const [activeTab, setActiveTab] = useState(() => {
    if (bearbeiten) return 'daten';
    const t = new URLSearchParams(location.search).get('tab');
    return REITER_AUS_ADRESSE[t] || 'wk';
  });

  // Turnier-Anmeldung State
  const [showTurnierModal, setShowTurnierModal] = useState(false);
  const [showTurnierAuswahl, setShowTurnierAuswahl] = useState(false);
  const [verfuegbareTurniere, setVerfuegbareTurniere] = useState([]);
  const [selectedTurnierForAnmeldung, setSelectedTurnierForAnmeldung] = useState(null);
  const [loadingTurniere, setLoadingTurniere] = useState(false);

  // Personal/Staff State
  const [personal, setPersonal] = useState([]);
  // Personal des Verbands ohne Vereinszuordnung (Altbestand, Verbandspersonal)
  const [personalFrei, setPersonalFrei] = useState([]);
  const [freiOffen, setFreiOffen] = useState(false);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [newStaff, setNewStaff] = useState({
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    primaer_rolle: 'helfer',
    qualifikation: 'keine',
    lizenz_nummer: '',
    verband: '',
    notizen: ''
  });

  // Anmeldungen State
  const [anmeldungen, setAnmeldungen] = useState([]);
  const [loadingAnmeldungen, setLoadingAnmeldungen] = useState(false);
  const [editingAnmeldung, setEditingAnmeldung] = useState(null);
  const [offenerStarter, setOffenerStarter] = useState(null); // "turnierId-wettkaempferId"

  // Dojo Sync State
  const [hasDojo, setHasDojo] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  // State für das neue Mitglied
  const [newMember, setNewMember] = useState({
    vorname: "",
    nachname: "",
    geburtsdatum: "",
    gewicht: "",
    skill_level: "",
    email: "",
    handy: "",
    vereins_id: id,
    geschlecht: "",
    nationalitaet: "",
    gurtfarbe: "",
    kampfstil: ""
  });

  const aktion = useVereinAktionen();

  // /bearbeiten in der Adresse wechselt auch bei schon offener Seite in den Bearbeitungsmodus
  useEffect(() => {
    if (bearbeiten && verein) {
      setActiveTab('daten');
      setEditedVerein(leererVerein(verein));
      setFeldFehler({});
      setIsEditing(true);
    }
  }, [bearbeiten, verein]);

  // Seit 22.10.2026 nur das Personal dieses Vereins (turnier_personal.verein_id)
  const fetchPersonal = async () => {
    try {
      const [eigen, frei] = await Promise.all([
        apiJson(`/api/personal?verein_id=${id}`),
        apiJson(`/api/personal?ohne_verein=1&fuer_verein=${id}`),
      ]);
      setPersonal(Array.isArray(eigen?.data) ? eigen.data : []);
      setPersonalFrei(Array.isArray(frei?.data) ? frei.data : []);
    } catch (err) {
      console.error('Fehler beim Laden des Personals:', err);
      setPersonal([]);
      setPersonalFrei([]);
    }
  };

  const personalZuordnen = async (p, vereinId) => {
    try {
      await apiJson(`/api/personal/${p.personal_id}/verein`, { method: 'PATCH', body: JSON.stringify({ verein_id: vereinId }) });
      toast.ok(vereinId ? `${p.vorname} ${p.nachname} ist jetzt bei ${verein.name}` : `${p.vorname} ${p.nachname} gehört wieder zum Verband`);
      await fetchPersonal();
    } catch (e) {
      toast.fehler(e.message);
    }
  };

  const fetchAnmeldungen = async () => {
    if (!id) return;
    setLoadingAnmeldungen(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/anmeldungen/verein/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // API gibt { success, data: { anmeldungen: [...] } } zurück
        if (data.success && data.data && Array.isArray(data.data.anmeldungen)) {
          setAnmeldungen(data.data.anmeldungen);
        } else if (data.success && Array.isArray(data.data)) {
          setAnmeldungen(data.data);
        } else if (Array.isArray(data)) {
          setAnmeldungen(data);
        } else {
          setAnmeldungen([]);
        }
      } else {
        setAnmeldungen([]);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Anmeldungen:', err);
      setAnmeldungen([]);
    } finally {
      setLoadingAnmeldungen(false);
    }
  };

  // Anmeldung bearbeiten - öffnet WettkampferAuswahlModal mit vorausgewähltem Wettkämpfer
  const handleEditAnmeldung = async (anmeldung) => {
    // Turnier-Daten für das Modal vorbereiten
    const turnierForEdit = {
      turnier_id: anmeldung.turnier_id,
      name: anmeldung.turnier_name,
      datum: anmeldung.turnier_datum,
      ort: anmeldung.turnier_ort
    };

    // Alle bereits angemeldeten Kategorien für diesen Wettkämpfer im Turnier laden
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/anmeldungen/wettkampfer/${anmeldung.wettkaempfer_id}/turnier/${anmeldung.turnier_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      let existingDivisions = [];
      if (res.ok) {
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          existingDivisions = data.data.map(a => a.division_code).filter(Boolean);
        }
      }

      // Speichere welcher Wettkämpfer bearbeitet wird mit allen bereits angemeldeten Kategorien
      setEditingAnmeldung({
        ...anmeldung,
        wettkaempfer_id: anmeldung.wettkaempfer_id,
        existingDivisions: existingDivisions
      });

      setSelectedTurnierForAnmeldung(turnierForEdit);
      setShowTurnierModal(true);
    } catch (err) {
      console.error('Fehler beim Laden der bestehenden Anmeldungen:', err);
      // Trotzdem fortfahren mit nur der aktuellen Division
      setEditingAnmeldung({
        ...anmeldung,
        wettkaempfer_id: anmeldung.wettkaempfer_id,
        existingDivisions: anmeldung.division_code ? [anmeldung.division_code] : []
      });
      setSelectedTurnierForAnmeldung(turnierForEdit);
      setShowTurnierModal(true);
    }
  };

  const stornieren = async (anmeldungId) => {
    await apiJson(`/api/anmeldungen/cancel/${anmeldungId}`, {
      method: 'PUT',
      body: JSON.stringify({ grund: 'In der Vereinsverwaltung storniert' })
    });
    setDialog(null);
    toast.ok('Meldung storniert');
    await fetchAnmeldungen();
  };

  const ladeVerein = useCallback(async () => {
    setLadeFehler(null);
    try {
      const d = await apiJson(`/api/vereine/${id}`);
      setVerein(d?.data || null);
      if (!d?.data) setLadeFehler({ message: 'Verein nicht gefunden' });
    } catch (e) {
      setLadeFehler(e);
    }
  }, [id]);

  useEffect(() => {
    setVerein(null);
    ladeVerein();
    getWettkaempferByVerein(id)
      .then((l) => setWettkaempfer(Array.isArray(l) ? l : []))
      .catch(() => setWettkaempfer([]));

    // Fetch Personal
    fetchPersonal();

    // Fetch Anmeldungen
    fetchAnmeldungen();

    // Check for Dojo integration
    checkDojoIntegration();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- soll nur bei id neu laufen, nicht wenn sich die Ladefunktion neu bildet
  }, [id]);

  const checkDojoIntegration = async () => {
    try {
      const response = await fetch(`/api/dojo-sync/check-updates/${id}`);
      if (!response.ok) return;
      const data = await response.json();

      if (data.success && data.has_integration) {
        setHasDojo(true);
        setLastSync(data.last_sync);
      }
    } catch (error) {
      console.error('Error checking dojo integration:', error);
    }
  };

  const handleDojoSync = async () => {
    setSyncing(true);
    setSyncMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/dojo-sync/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ verein_id: id })
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        setSyncMessage({
          type: 'ok',
          text: `Abgeglichen: ${data.imported} neu, ${data.updated} aktualisiert${data.errors > 0 ? `, ${data.errors} Fehler` : ''}`
        });
        setLastSync(data.last_sync);

        // Reload wettkaempfer list
        const list = await getWettkaempferByVerein(id);
        setWettkaempfer(list);

        // Refresh sync status
        checkDojoIntegration();
      } else {
        setSyncMessage({
          type: 'fehler',
          text: `Abgleich fehlgeschlagen: ${data.message || data.error || `Fehler ${response.status}`}`
        });
      }
    } catch (error) {
      setSyncMessage({
        type: 'fehler',
        text: `Abgleich fehlgeschlagen: ${error.message}`
      });
    } finally {
      setSyncing(false);

      // Clear message after 5 seconds
      setTimeout(() => setSyncMessage(null), 5000);
    }
  };

  const handleStartEdit = () => {
    setActiveTab('daten');
    setEditedVerein(leererVerein(verein));
    setFeldFehler({});
    setSpeicherFehler('');
    setIsEditing(true);
  };

  const ohneBearbeitenAdresse = () => {
    if (/\/bearbeiten\/?$/.test(location.pathname)) navigate(`/dashboard/vereine/${id}`, { replace: true });
  };

  const handleCancelEdit = () => {
    setEditedVerein(null);
    setFeldFehler({});
    setSpeicherFehler('');
    setIsEditing(false);
    ohneBearbeitenAdresse();
  };

  const handleSaveVerein = async () => {
    const f = pruefeVereinsdaten(editedVerein);
    setFeldFehler(f);
    setSpeicherFehler('');
    if (Object.keys(f).length) {
      document.getElementById(`vv-f-${Object.keys(f)[0] === 'email' ? 'email' : Object.keys(f)[0]}`)?.focus();
      return;
    }
    try {
      setSaving(true);
      const body = Object.fromEntries(Object.entries(editedVerein).map(([k, w]) => [k, typeof w === 'string' ? w.trim() : w]));
      const d = await apiJson(`/api/vereine/${id}`, { method: 'PUT', body: JSON.stringify(body) });
      setVerein((alt) => ({ ...alt, ...body, ...(d?.data || {}) }));
      setIsEditing(false);
      setEditedVerein(null);
      toast.ok('Vereinsdaten gespeichert');
      ohneBearbeitenAdresse();
    } catch (error) {
      if (error.felder) setFeldFehler(error.felder);
      setSpeicherFehler(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      setLogoUploading(true);
      const formData = new FormData();
      formData.append('logo', file);
      const token = localStorage.getItem('token');
      const res = await fetch('/api/upload/logo', {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Upload fehlgeschlagen');
      const data = await res.json();
      const logoPath = data.data?.logo || data.logo || data.url || data.path;
      setEditedVerein(prev => ({ ...prev, logo: logoPath }));
    } catch (err) {
      toast.fehler('Logo-Upload fehlgeschlagen: ' + err.message);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleOpenAddModal = () => {
    setNewMember({
      vorname: "",
      nachname: "",
      geburtsdatum: "",
      gewicht: "",
      skill_level: "",
      email: "",
      handy: "",
      vereins_id: id,
      geschlecht: "",
      nationalitaet: "",
      gurtfarbe: "",
      kampfstil: ""
    });
    setModalError('');
    setShowAddModal(true);
  };

  // Formular absenden
  const handleWettkaempferSubmit = async (e) => {
    e.preventDefault();
    setModalError('');
    setSaving(true);

    // Pflichtfelder prüfen
    if (!newMember.vorname || !newMember.nachname || !newMember.geburtsdatum || !newMember.skill_level || !newMember.geschlecht || !newMember.gurtfarbe) {
      setModalError("Bitte alle Pflichtfelder ausfüllen.");
      setSaving(false);
      return;
    }

    try {
      // Vereinside IMMER mitgeben!
      const saveObj = { ...newMember, vereins_id: id };
      const saved = await addWettkaempfer(saveObj);
      if (saved && (saved.id || saved.wettkaempfer_id)) {
        const list = await getWettkaempferByVerein(id);
        setWettkaempfer(list);
        setShowAddModal(false);
      } else {
        setModalError("Speichern fehlgeschlagen.");
      }
    } catch (err) {
      setModalError("Fehler beim Speichern: " + (err.message || ""));
    }
    setSaving(false);
  };

  // Meldungen je Wettkämpfer (nicht storniert)
  const meldungenJeWk = useMemo(() => {
    const m = {};
    anmeldungen.forEach((a) => { if (!istStorniert(a)) m[a.wettkaempfer_id] = (m[a.wettkaempfer_id] || 0) + 1; });
    return m;
  }, [anmeldungen]);

  const gurte = useMemo(() => [...new Set(wettkaempfer.map((w) => w.gurtfarbe).filter(Boolean))], [wettkaempfer]);
  const filteredWettkaempfer = useMemo(() => {
    const s = searchQuery.trim().toLowerCase();
    return wettkaempfer
      .filter((wk) => (!s || `${wk.vorname} ${wk.nachname}`.toLowerCase().includes(s))
        && (!wkGurt || wk.gurtfarbe === wkGurt)
        && (!wkG || String(wk.geschlecht || '').toLowerCase().startsWith(wkG)))
      .sort((a, b) => String(a.nachname || '').localeCompare(String(b.nachname || ''), 'de') || String(a.vorname || '').localeCompare(String(b.vorname || ''), 'de'));
  }, [wettkaempfer, searchQuery, wkGurt, wkG]);

  // Meldungen nach Turnier, darin nach Wettkämpfer
  const turniereMitMeldung = useMemo(() => {
    const t = {};
    anmeldungen.forEach((a) => {
      const k = a.turnier_id || 'unbekannt';
      if (!t[k]) {
        t[k] = {
          turnier_id: a.turnier_id, turnier_name: a.turnier_name || 'Unbekanntes Turnier', turnier_datum: a.turnier_datum, turnier_ort: a.turnier_ort,
          preis_einzel: parseFloat(a.preis_einzel) || 25, preis_team: parseFloat(a.preis_team) || 40,
          wk: {}, starts: 0, storniert: 0, bestaetigt: 0,
        };
      }
      if (istStorniert(a)) { t[k].storniert += 1; return; }
      t[k].starts += 1;
      if (a.status === 'Bestätigt') t[k].bestaetigt += 1;
      if (!t[k].wk[a.wettkaempfer_id]) t[k].wk[a.wettkaempfer_id] = { wettkaempfer_id: a.wettkaempfer_id, vorname: a.vorname, nachname: a.nachname, kategorien: [] };
      t[k].wk[a.wettkaempfer_id].kategorien.push(a);
    });
    return Object.values(t).sort((x, y) => new Date(y.turnier_datum || 0) - new Date(x.turnier_datum || 0));
  }, [anmeldungen]);

  if (!verein) {
    return (
      <div className="vv">
        <button className="vv-zurueck" onClick={() => navigate('/dashboard/vereine')}><Sym n="links" /> Alle Vereine</button>
        {ladeFehler ? (
          <LeerKarte sym="wolke" fehler titel={ladeFehler.status === 404 || !ladeFehler.status ? 'Verein nicht gefunden' : 'Verein konnte nicht geladen werden'} text={ladeFehler.message} technisch={ladeFehler.technisch}>
            <button className="vv-k vv-k-haupt" onClick={ladeVerein}><Sym n="neu" /> Erneut versuchen</button>
          </LeerKarte>
        ) : (
          <div aria-busy="true" aria-label="Verein wird geladen">
            <div className="vv-d-kopf"><span className="vv-skel" style={{ width: 64, height: 64 }} /><div><span className="vv-skel" style={{ height: 24, width: '50%', marginBottom: 10 }} /><span className="vv-skel" style={{ height: 12, width: '35%' }} /></div><span /></div>
            <div className="vv-d-zahlen">{[0, 1, 2, 3, 4].map((i) => <div className="vv-d-zahl" key={i}><span className="vv-skel" style={{ height: 44 }} /></div>)}</div>
          </div>
        )}
      </div>
    );
  }


  // ===== WETTKÄMPFER ACTION HANDLERS =====
  const handleEditWettkaempfer = (wk) => {
    const formattedDate = wk.geburtsdatum ? wk.geburtsdatum.split('T')[0] : '';
    setEditingWettkaempfer({...wk, geburtsdatum: formattedDate});
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e?.preventDefault?.();
    if (!editingWettkaempfer) return;
    setSaving(true);
    setModalError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/wettkaempfer/${editingWettkaempfer.wettkaempfer_id}`, {
        method: 'PUT',
        headers: {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'},
        body: JSON.stringify(editingWettkaempfer)
      });
      if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Fehler');
      setWettkaempfer(await getWettkaempferByVerein(id));
      setShowEditModal(false);
      setEditingWettkaempfer(null);
      toast.ok('Wettkämpfer aktualisiert');
    } catch (err) {
      setModalError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleShowDetail = (wk) => {
    const formattedDate = wk.geburtsdatum ? wk.geburtsdatum.split('T')[0] : '';
    setDetailWettkaempfer({...wk, geburtsdatum: formattedDate});
    setShowDetailModal(true);
  };

  const handleEditFromDetail = () => {
    setEditingWettkaempfer(detailWettkaempfer);
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  // 21.10.2026: Das Foto geht erst hoch, wenn beantwortet ist, ob es auf
  // Hallenbildschirmen gezeigt werden darf (components/fotos/FotoHallenEinwilligung).
  const handleFotoUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (file) setFotoDatei(file);
  };

  const fotoAnzeigeGespeichert = async (felder) => {
    setFotoDatei(null);
    setDetailWettkaempfer(prev => ({ ...prev, ...felder }));
    toast.ok(felder.foto ? 'Foto gespeichert' : 'Gespeichert');
    try {
      setWettkaempfer(await getWettkaempferByVerein(id));
    } catch (err) {
      toast.fehler('Liste nicht neu geladen: ' + err.message);
    }
  };

  const wkLoeschen = async (wk) => {
    await apiJson(`/api/wettkaempfer/${wk.wettkaempfer_id}`, { method: 'DELETE' });
    setDialog(null);
    setWettkaempfer(await getWettkaempferByVerein(id));
    fetchAnmeldungen();
    toast.ok(`${wk.vorname} ${wk.nachname} gelöscht`);
  };

  // ===== STAFF/PERSONAL HANDLERS =====
  const handleOpenAddStaffModal = () => {
    setNewStaff({
      vorname: '',
      nachname: '',
      email: '',
      telefon: '',
      primaer_rolle: 'helfer',
      qualifikation: 'keine',
      lizenz_nummer: '',
      verband: '',
      notizen: ''
    });
    setModalError('');
    setShowAddStaffModal(true);
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    setModalError('');
    setSaving(true);

    if (!newStaff.vorname || !newStaff.nachname) {
      setModalError("Vor- und Nachname sind erforderlich.");
      setSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/personal', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        // Vom Verein gemeldet = dem Verein zugeordnet (22.10.2026)
        body: JSON.stringify({ ...newStaff, verein_id: id })
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        await fetchPersonal();
        setShowAddStaffModal(false);
      } else {
        setModalError(data.message || data.error || 'Fehler beim Speichern');
      }
    } catch (err) {
      setModalError("Fehler: " + err.message);
    }
    setSaving(false);
  };

  const personalDeaktivieren = async (p) => {
    await apiJson(`/api/personal/${p.personal_id}`, { method: 'DELETE' });
    setDialog(null);
    await fetchPersonal();
    toast.ok(`${p.vorname} ${p.nachname} deaktiviert`);
  };

  const getRolleInfo = (rolle) => STAFF_ROLLEN.find(r => r.value === rolle) || (rolle ? { value: rolle, label: rolle.charAt(0).toUpperCase() + rolle.slice(1) } : STAFF_ROLLEN[6]);
  const getQualInfo = (qual) => QUALIFIKATIONEN.find(q => q.value === qual) || QUALIFIKATIONEN[0];

  // ===== TURNIER-ANMELDUNG FUNKTIONEN =====
  const fetchVerfuegbareTurniere = async () => {
    setLoadingTurniere(true);
    try {
      const token = localStorage.getItem('token');
      const clubDataStr = localStorage.getItem('clubData');
      let authToken = token;

      if (clubDataStr) {
        const clubData = JSON.parse(clubDataStr);
        authToken = clubData.token || token;
      }

      const isAdmin = !!localStorage.getItem('adminData');
      const params = new URLSearchParams(isAdmin ? {} : { zukunft: 'true' });

      const response = await fetch(`/api/turniere?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      let turniereData = [];

      if (data.success && Array.isArray(data.data)) {
        turniereData = data.data;
      } else if (Array.isArray(data)) {
        turniereData = data;
      }

      // Turniere anzeigen: nicht abgesagt, nicht geschlossen
      const offeneTurniere = turniereData.filter(t =>
        !['Abgesagt', 'Geschlossen', 'Abgeschlossen'].includes(t.status)
      );
      setVerfuegbareTurniere(offeneTurniere);

    } catch (error) {
      console.error('Fehler beim Laden der Turniere:', error);
      toast.fehler('Turniere konnten nicht geladen werden: ' + error.message);
      setVerfuegbareTurniere([]);
    } finally {
      setLoadingTurniere(false);
    }
  };

  const handleOpenTurnierAnmeldung = async () => {
    setShowTurnierAuswahl(true);
    await fetchVerfuegbareTurniere();
  };

  const handleTurnierSelected = (turnier) => {
    setSelectedTurnierForAnmeldung(turnier);
    setShowTurnierAuswahl(false);
    setShowTurnierModal(true);
  };

  const handleAnmeldungAbgeschlossen = (anmeldungData) => {
    setShowTurnierModal(false);
    setSelectedTurnierForAnmeldung(null);
    toast.ok(`Anmeldung erfolgreich!\n\n${anmeldungData.wettkampfer?.length || 0} Wettkämpfer wurden für das Turnier "${anmeldungData.turnier?.name}" angemeldet.`);
  };

  // ===== VEREIN: SPERREN, ARCHIVIEREN, LÖSCHEN =====
  const istKonto = istVerein(verein);
  const gesperrt = verein.status === 'gesperrt';
  const aktiveMeldungen = anmeldungen.filter((a) => !istStorniert(a)).length;
  const oeffneDialog = (art, daten = verein) => { setMenue(false); setDialog({ art, daten }); };
  const zu = () => setDialog(null);
  const nachStatus = async (meldung) => { setDialog(null); toast.ok(meldung); await ladeVerein(); };

  // ===== Zahlen =====
  const anzahlWk = wettkaempfer.length;
  const jugend = wettkaempfer.filter((w) => { const a = alterAus(w.geburtsdatum); return a !== null && a < 18; }).length;
  const maennlich = wettkaempfer.filter((w) => String(w.geschlecht || '').toLowerCase().startsWith('m')).length;
  const weiblich = wettkaempfer.filter((w) => String(w.geschlecht || '').toLowerCase().startsWith('w')).length;
  const ohneGew = wettkaempfer.filter((w) => !(Number(w.gewicht) > 0)).length;
  const loginTage = tageSeit(verein.letzter_login);
  const ev = isEditing ? (editedVerein || leererVerein(verein)) : leererVerein(verein);
  const setze = (k) => (w) => { setEditedVerein((alt) => ({ ...(alt || leererVerein(verein)), [k]: w })); if (feldFehler[k]) setFeldFehler((f) => { const n = { ...f }; delete n[k]; return n; }); };
  const logoBild = (isEditing ? editedVerein?.logo : verein.logo) || null;

  const reiter = [
    ['wk', 'Wettkämpfer', anzahlWk],
    ['anm', 'Anmeldungen', aktiveMeldungen],
    ['helfer', 'Helfer & Kampfrichter', personal.length],
    ['daten', 'Vereinsdaten', null],
  ];

  const wechsleReiter = (k) => {
    if (isEditing && k !== 'daten') { handleCancelEdit(); }
    setActiveTab(k);
  };

  // ===== Reiter: Wettkämpfer =====
  const tabWk = () => {
    if (!anzahlWk) {
      return (
        <LeerKarte sym="leute" titel="Noch keine Wettkämpfer" text={`${verein.name} hat noch niemanden eingetragen. Wettkämpfer kannst du hier anlegen${hasDojo ? ' oder aus der Dojosoftware übernehmen' : ''}.`}>
          {hasDojo && <button className="vv-k" onClick={handleDojoSync} disabled={syncing}><Sym n="neu" /> {syncing ? 'Gleicht ab …' : 'Mit Dojosoftware abgleichen'}</button>}
          <button className="vv-k vv-k-haupt" onClick={handleOpenAddModal}><Sym n="plus" /> Wettkämpfer anlegen</button>
        </LeerKarte>
      );
    }
    return (
      <>
        {ohneGew > anzahlWk / 2 && (
          <div className="vv-hinweis"><Sym n="achtung" /><div><b>{ohneGew} von {anzahlWk} ohne Gewicht.</b> Für Leichtkontakt und Vollkontakt braucht die Einteilung das Gewicht. Der Verein kann es selbst nachtragen.</div></div>
        )}
        {syncMessage && <div className={`vv-meldung-zeile vv-${syncMessage.type}`} role="status">{syncMessage.text}</div>}
        <div className="vv-werkzeug">
          <div className="vv-suche"><Sym n="suche" /><input type="search" placeholder="Wettkämpfer suchen" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} aria-label="Wettkämpfer suchen" /></div>
          <select className="vv-wahl" value={wkGurt} onChange={(e) => setWkGurt(e.target.value)} aria-label="Gurt">
            <option value="">Jeder Gurt</option>
            {gurte.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <div className="vv-seg" role="group" aria-label="Geschlecht">
            <button aria-pressed={!wkG} onClick={() => setWkG('')}>Alle</button>
            <button aria-pressed={wkG === 'm'} onClick={() => setWkG('m')}>m</button>
            <button aria-pressed={wkG === 'w'} onClick={() => setWkG('w')}>w</button>
          </div>
          <span className="vv-anzahl">{filteredWettkaempfer.length} von {anzahlWk}</span>
          {hasDojo && (
            <button className="vv-k" onClick={handleDojoSync} disabled={syncing} title={lastSync ? `Zuletzt abgeglichen ${new Date(lastSync).toLocaleString('de-DE')}` : 'Noch nie abgeglichen'}>
              <Sym n="neu" /> {syncing ? 'Gleicht ab …' : 'Mit Dojosoftware abgleichen'}
            </button>
          )}
          <button className="vv-k vv-k-haupt" onClick={handleOpenAddModal}><Sym n="plus" /> Wettkämpfer</button>
        </div>
        <div className="vv-tab-rahmen" style={{ maxHeight: 620 }}>
          <table className="vv-t vv-wk-tab">
            <thead><tr><th>Name</th><th>Jahrgang</th><th>Gurt</th><th className="vv-r">Gewicht</th><th className="vv-r">Meldungen</th><th /></tr></thead>
            <tbody>
              {filteredWettkaempfer.map((wk) => {
                const jg = jahrgang(wk.geburtsdatum);
                const alter = alterAus(wk.geburtsdatum);
                const g = String(wk.geschlecht || '').toLowerCase().charAt(0);
                return (
                  <tr key={wk.wettkaempfer_id} className="vv-wk-klick" onClick={() => handleShowDetail(wk)}>
                    <td className="vv-name">{wk.vorname} {wk.nachname} {g && <span className="vv-art" style={{ marginLeft: 4 }}>{g}</span>}</td>
                    <td className="vv-leise vv-c-jg">{jg ? `${jg} · ${alter} J.` : <Fehlt>Geburtsdatum fehlt</Fehlt>}</td>
                    <td className="vv-c-gurt">{wk.gurtfarbe ? <span className="vv-gurt"><i style={{ background: gurtHintergrund(wk.gurtfarbe) }} />{wk.gurtfarbe}</span> : <Fehlt>fehlt</Fehlt>}</td>
                    <td className="vv-r vv-c-gew">{Number(wk.gewicht) > 0 ? `${Number(wk.gewicht).toLocaleString('de-DE')} kg` : <Fehlt>fehlt</Fehlt>}</td>
                    <td className="vv-r vv-zahl-t vv-c-anm">{meldungenJeWk[wk.wettkaempfer_id] || '–'}</td>
                    <td className="vv-c-akt" onClick={(e) => e.stopPropagation()}>
                      <div className="vv-zeil-akt">
                        <button className="vv-k vv-k-leise vv-k-klein" title="Bearbeiten" aria-label={`${wk.vorname} ${wk.nachname} bearbeiten`} onClick={() => handleEditWettkaempfer(wk)}><Sym n="stift" /></button>
                        <button className="vv-k vv-k-leise vv-k-klein" title="Löschen" aria-label={`${wk.vorname} ${wk.nachname} löschen`} onClick={() => oeffneDialog('wkLoeschen', wk)}><Sym n="muell" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filteredWettkaempfer.length && <tr><td colSpan={6} className="vv-t-leer">Niemand passt zu diesem Filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  // ===== Reiter: Anmeldungen =====
  const tabAnm = () => {
    if (loadingAnmeldungen && !anmeldungen.length) {
      return <div className="vv-zeilen" aria-busy="true">{[0, 1, 2].map((i) => <div className="vv-zeile" key={i} style={{ cursor: 'default', gridTemplateColumns: '58px 1fr' }}><span className="vv-skel" style={{ height: 40 }} /><span className="vv-skel" style={{ height: 16, width: '50%' }} /></div>)}</div>;
    }
    if (!turniereMitMeldung.some((t) => t.starts > 0)) {
      return (
        <LeerKarte sym="pokal" titel="Noch keine Anmeldungen" text="Dieser Verein hat bisher niemanden zu einem Turnier gemeldet.">
          <button className="vv-k vv-k-haupt" onClick={handleOpenTurnierAnmeldung}><Sym n="pokal" /> Zum Turnier melden</button>
        </LeerKarte>
      );
    }
    const heute = new Date(); heute.setHours(0, 0, 0, 0);
    return turniereMitMeldung.filter((t) => t.starts > 0).map((t) => {
      const d = t.turnier_datum ? new Date(t.turnier_datum) : null;
      const vorbei = d ? d < heute : false;
      const liste = Object.values(t.wk).sort((a, b) => String(a.nachname).localeCompare(String(b.nachname), 'de'));
      const preis = liste.reduce((s, w) => {
        const gespeichert = w.kategorien.every((k) => k.startgebuehr !== null && k.startgebuehr !== undefined);
        return s + (gespeichert ? w.kategorien.reduce((x, k) => x + Number(k.startgebuehr), 0) : calculatePrice(w.kategorien.length, t.preis_einzel, t.preis_team));
      }, 0);
      const alleBestaetigt = t.bestaetigt === t.starts;
      const offen = liste.find((w) => offenerStarter === `${t.turnier_id}-${w.wettkaempfer_id}`);
      return (
        <div className="vv-turnier" key={t.turnier_id}>
          <div className="vv-turnier-kopf">
            <div className="vv-dat">{d ? <><b>{d.getDate()}</b><small>{MONATE[d.getMonth()]} {d.getFullYear()}</small></> : <small>ohne Datum</small>}</div>
            <div style={{ minWidth: 0 }}>
              <strong>{t.turnier_name}</strong>
              <span className="vv-z-unter">
                {[`${t.starts} ${t.starts === 1 ? 'Start' : 'Starts'}`, `${liste.length} ${liste.length === 1 ? 'Teilnehmer' : 'Teilnehmer'}`, formatCurrency(preis), t.turnier_ort, vorbei ? 'abgeschlossen' : null, t.storniert ? `${t.storniert} storniert` : null].filter(Boolean).join(' · ')}
              </span>
            </div>
            <div className="vv-turnier-akt">
              <span className={`vv-st ${alleBestaetigt ? 'vv-st-aktiv' : 'vv-st-nie'}`}>{alleBestaetigt ? 'Bestätigt' : 'Angemeldet'}</span>
              {!vorbei && <button className="vv-k vv-k-klein" onClick={() => handleTurnierSelected({ turnier_id: t.turnier_id, name: t.turnier_name, datum: t.turnier_datum, ort: t.turnier_ort })}>Meldung ändern</button>}
            </div>
          </div>
          <div className="vv-turnier-starter">
            {liste.map((w) => {
              const k = `${t.turnier_id}-${w.wettkaempfer_id}`;
              const ohneKat = w.kategorien.some((x) => !x.division_code);
              return (
                <button key={k} className={`vv-marke${ohneKat ? ' vv-gelb' : ''}`} aria-expanded={offenerStarter === k}
                  onClick={() => setOffenerStarter(offenerStarter === k ? null : k)}
                  title={ohneKat ? 'Mindestens eine Meldung ohne Kategorie' : `${w.kategorien.length} Kategorien`}>
                  {w.vorname} {String(w.nachname || '').charAt(0)}.{w.kategorien.length > 1 ? ` ×${w.kategorien.length}` : ''}
                </button>
              );
            })}
          </div>
          {offen && (
            <div className="vv-turnier-einzeln">
              <table className="vv-t">
                <thead><tr><th>{offen.vorname} {offen.nachname}: Kategorie</th><th>Status</th><th className="vv-r">Gebühr</th><th /></tr></thead>
                <tbody>
                  {offen.kategorien.map((k) => (
                    <tr key={k.anmeldung_id}>
                      <td>{k.division_code || <Fehlt>keine Kategorie</Fehlt>}</td>
                      <td><span className={`vv-st ${k.status === 'Bestätigt' ? 'vv-st-aktiv' : 'vv-st-nie'}`}>{k.status || 'Angemeldet'}</span></td>
                      <td className="vv-r vv-zahl-t">{k.startgebuehr !== null && k.startgebuehr !== undefined ? formatCurrency(Number(k.startgebuehr)) : '–'}</td>
                      <td className="vv-r">
                        {!vorbei && <button className="vv-k vv-k-klein vv-k-gefahr" onClick={() => oeffneDialog('storno', { ...k, name: `${offen.vorname} ${offen.nachname}` })}>Stornieren</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!vorbei && (
                <div style={{ padding: '8px 14px 12px' }}>
                  <button className="vv-k vv-k-klein" onClick={() => handleEditAnmeldung({ ...offen.kategorien[0], wettkaempfer_id: offen.wettkaempfer_id, vorname: offen.vorname, nachname: offen.nachname, turnier_id: t.turnier_id, turnier_name: t.turnier_name, turnier_datum: t.turnier_datum, turnier_ort: t.turnier_ort })}>
                    <Sym n="stift" /> Kategorien von {offen.vorname} ändern
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  // ===== Reiter: Helfer & Kampfrichter =====
  const tabHelfer = () => (
    <>
      {!personal.length ? (
        <LeerKarte sym="person" titel="Noch keine Helfer oder Kampfrichter" text="Trag ein, wen der Verein am Turniertag stellt: Kampfrichter mit Lizenz, Zeitnehmer, Schreiber, Sanitäter. Die Turnierleitung sieht sie dann in der Einteilung.">
          <button className="vv-k vv-k-haupt" onClick={handleOpenAddStaffModal}><Sym n="plus" /> Helfer eintragen</button>
        </LeerKarte>
      ) : (
        <>
          <div className="vv-werkzeug"><span className="vv-anzahl">{personal.length} {personal.length === 1 ? 'Person' : 'Personen'}</span><button className="vv-k vv-k-haupt" onClick={handleOpenAddStaffModal}><Sym n="plus" /> Helfer eintragen</button></div>
          <div className="vv-tab-rahmen">
            <table className="vv-t">
              <thead><tr><th>Name</th><th>Rolle</th><th>Lizenz</th><th>Kontakt</th><th className="vv-r">Einsätze</th><th /></tr></thead>
              <tbody>
                {personal.map((p) => (
                  <tr key={p.personal_id}>
                    <td className="vv-name">{p.vorname} {p.nachname}</td>
                    <td className="vv-leise">{getRolleInfo(p.primaer_rolle).label}</td>
                    <td className="vv-leise">{getQualInfo(p.qualifikation).label}{p.lizenz_nummer ? ` · ${p.lizenz_nummer}` : ''}</td>
                    <td className="vv-leise">{[p.email, p.telefon].filter(Boolean).join(' · ') || '–'}</td>
                    <td className="vv-r vv-zahl-t">{p.anzahl_turniere || '–'}</td>
                    <td className="vv-r">
                      <div className="vv-zeil-akt">
                        <button className="vv-k vv-k-leise vv-k-klein" onClick={() => personalZuordnen(p, null)} title="Dem Verband zurückgeben" aria-label={`${p.vorname} ${p.nachname} dem Verband zurückgeben`}><Sym n="links" /></button>
                        <button className="vv-k vv-k-leise vv-k-klein" onClick={() => oeffneDialog('personal', p)} aria-label={`${p.vorname} ${p.nachname} deaktivieren`} title="Deaktivieren"><Sym n="muell" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {personalFrei.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div className="vv-hinweis vv-hinweis-blau">
            <Sym n="leute" />
            <div style={{ flex: 1 }}>
              <b>{personalFrei.length} {personalFrei.length === 1 ? 'Person' : 'Personen'} im Verband ohne Verein.</b> Altbestand von vor der Zuordnung oder Personal des Verbands selbst. Wer von diesem Verein kommt, lässt sich hier zuordnen.
              <div><button className="vv-k vv-k-klein" style={{ marginTop: 8 }} onClick={() => setFreiOffen(!freiOffen)} aria-expanded={freiOffen}>{freiOffen ? 'Liste schließen' : 'Liste zeigen'}</button></div>
            </div>
          </div>
          {freiOffen && (
            <div className="vv-tab-rahmen">
              <table className="vv-t">
                <thead><tr><th>Name</th><th>Rolle</th><th>Kontakt</th><th /></tr></thead>
                <tbody>
                  {personalFrei.map((p) => (
                    <tr key={p.personal_id}>
                      <td className="vv-name">{p.vorname} {p.nachname}</td>
                      <td className="vv-leise">{getRolleInfo(p.primaer_rolle).label}</td>
                      <td className="vv-leise">{[p.email, p.telefon].filter(Boolean).join(' · ') || '–'}</td>
                      <td className="vv-r"><button className="vv-k vv-k-klein" onClick={() => personalZuordnen(p, verein.vereins_id)}><Sym n="plus" /> Diesem Verein zuordnen</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );

  // ===== Reiter: Vereinsdaten =====
  const tabDaten = () => {
    const lesen = !isEditing;
    const F = feldFehler;
    return (
      <div className={`vv-form${lesen ? ' vv-lese' : ''}`}>
        <div>
          {speicherFehler && <div className="vv-hinweis vv-hinweis-rot" role="alert"><Sym n="achtung" /><div><b>Nicht gespeichert.</b> {speicherFehler}</div></div>}
          {isEditing && Object.keys(F).length > 0 && !speicherFehler && <div className="vv-hinweis vv-hinweis-rot" role="alert"><Sym n="achtung" /><div>Bitte die markierten Felder prüfen.</div></div>}
          <div className="vv-form-teil"><h3>Verein</h3><div className="vv-felder">
            <Feld id="name" label="Name des Vereins" klasse="vv-s4" wert={ev.name} onWert={setze('name')} lesen={lesen} fehler={F.name} />
            <Feld id="benutzername" label="Benutzername" klasse="vv-s2" wert={verein.benutzername} lesen={lesen} nurLesen tipp="ändert der Verein selbst" />
            <Feld id="ansprechpartner" label="Ansprechpartner" klasse="vv-s6" wert={ev.ansprechpartner} onWert={setze('ansprechpartner')} lesen={lesen} fehler={F.ansprechpartner} />
          </div></div>
          <div className="vv-form-teil"><h3>Kontakt</h3><div className="vv-felder">
            <Feld id="email" label="E-Mail" typ="email" wert={ev.email} onWert={setze('email')} lesen={lesen} fehler={F.email} tipp="damit meldet sich der Verein an" />
            <Feld id="telefon" label="Telefon" typ="tel" wert={ev.telefon} onWert={setze('telefon')} lesen={lesen} fehler={F.telefon} />
            <Feld id="homepage" label="Homepage" klasse="vv-s6" wert={ev.homepage} onWert={setze('homepage')} lesen={lesen} fehler={F.homepage} />
          </div></div>
          <div className="vv-form-teil"><h3>Anschrift</h3><div className="vv-felder">
            <Feld id="strasse" label="Straße" klasse="vv-s4" wert={ev.strasse} onWert={setze('strasse')} lesen={lesen} fehler={F.strasse} />
            <Feld id="hausnummer" label="Nr." klasse="vv-s2" wert={ev.hausnummer} onWert={setze('hausnummer')} lesen={lesen} fehler={F.hausnummer} />
            <Feld id="plz" label="PLZ" klasse="vv-s2" wert={ev.plz} onWert={setze('plz')} lesen={lesen} fehler={F.plz} />
            <Feld id="ort" label="Ort" klasse="vv-s4" wert={ev.ort} onWert={setze('ort')} lesen={lesen} fehler={F.ort} />
          </div></div>
          {isEditing && (
            <div className="vv-form-fuss">
              <button className="vv-k" onClick={handleCancelEdit} disabled={saving}>Abbrechen</button>
              <button className="vv-k vv-k-haupt" onClick={handleSaveVerein} disabled={saving}><Sym n="haken" /> {saving ? 'Speichert …' : 'Speichern'}</button>
            </div>
          )}
          {!isEditing && (
            <div className="vv-form-fuss"><button className="vv-k" onClick={handleStartEdit}><Sym n="stift" /> Bearbeiten</button></div>
          )}
        </div>
        <div>
          <div className="vv-form-teil"><h3>Logo</h3>
            <div className="vv-logo-feld">
              {logoBild ? <img src={logoBild} alt="" className="vv-logo-bild" /> : <Siegel verein={verein} gross />}
              <div>
                {logoBild ? 'Erscheint auf Startlisten und Urkunden.' : 'Noch kein Logo. Es erscheint auf Startlisten und Urkunden.'}
                {isEditing ? (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <label className="vv-k vv-k-klein" style={{ cursor: logoUploading ? 'wait' : 'pointer' }}>
                      <Sym n="export" /> {logoUploading ? 'Lädt hoch …' : 'Bild hochladen'}
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} disabled={logoUploading} />
                    </label>
                    {editedVerein?.logo && <button className="vv-k vv-k-klein vv-k-gefahr" style={{ marginTop: 6 }} onClick={() => setEditedVerein((p) => ({ ...p, logo: null }))}>Entfernen</button>}
                  </div>
                ) : <div className="vv-tipp" style={{ marginTop: 4, color: 'var(--vv-text-3)' }}>Ändern über „Bearbeiten“.</div>}
              </div>
            </div>
          </div>
          <div className="vv-form-teil"><h3>Dojosoftware</h3>
            <div className="vv-klein-text">
              {hasDojo ? <>Verknüpft. Wettkämpfer werden aus der Dojosoftware übernommen.{lastSync && <> Zuletzt {new Date(lastSync).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.</>}<br />
                <button className="vv-k vv-k-klein" style={{ marginTop: 6 }} onClick={handleDojoSync} disabled={syncing}><Sym n="neu" /> {syncing ? 'Gleicht ab …' : 'Jetzt abgleichen'}</button></> : 'Nicht verknüpft.'}
            </div>
          </div>
          {istKonto && (
            <div className="vv-form-teil vv-gefahrzone"><h3>Konto verwalten</h3>
              <div className="vv-gz-zeile"><div>{gesperrt ? 'Zugang freigeben' : 'Zugang sperren'}<small>{gesperrt ? 'Der Verein kann sich wieder anmelden.' : 'Anmelden geht nicht mehr. Meldungen bleiben.'}</small></div>
                {gesperrt ? <button className="vv-k vv-k-klein" onClick={() => oeffneDialog('freigeben')}>Freigeben</button> : <button className="vv-k vv-k-klein" onClick={() => oeffneDialog('sperren')}>Sperren</button>}</div>
              <div className="vv-gz-zeile"><div>Archivieren<small>Aus dem Bestand nehmen, später zurückholbar.</small></div><button className="vv-k vv-k-klein" onClick={() => oeffneDialog('archiv')}>Archivieren</button></div>
              <div className="vv-gz-zeile"><div>Endgültig löschen<small>Mit allen Wettkämpfern. Nicht umkehrbar.</small></div><button className="vv-k vv-k-klein vv-k-gefahr" onClick={() => oeffneDialog('loeschen')}>Löschen</button></div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const inhalt = activeTab === 'wk' ? tabWk() : activeTab === 'anm' ? tabAnm() : activeTab === 'helfer' ? tabHelfer() : tabDaten();
  const dd = dialog?.daten;

  return (
    <>
    <div className="vv" onClick={() => { if (menue) setMenue(false); }}>
      <button className="vv-zurueck" onClick={() => navigate('/dashboard/vereine')}><Sym n="links" /> Alle Vereine</button>
      <div className="vv-d-kopf">
        {verein.logo ? <img src={verein.logo} alt="" className="vv-logo-bild" /> : <Siegel verein={verein} gross />}
        <div style={{ minWidth: 0 }}>
          <h1>{verein.name}</h1>
          <div className="vv-d-unter">
            <StatusAbzeichen verein={verein} />
            <span>{istKonto ? 'Vereinskonto' : 'Verwaltungskonto'}</span>
            {verein.erstellt_am && <><span>·</span><span>seit {datum(verein.erstellt_am)}</span></>}
            {hasDojo && <span className="vv-marke vv-blau">mit Dojosoftware verknüpft</span>}
          </div>
          <div className="vv-d-fakten">
            <span><Sym n="person" />{verein.ansprechpartner || '–'}</span>
            {verein.email && <span><Sym n="mail" />{verein.email}</span>}
            {verein.telefon && <span><Sym n="tel" />{verein.telefon}</span>}
            {plzOrt(verein) && <span><Sym n="ort" />{plzOrt(verein)}</span>}
          </div>
        </div>
        <div className="vv-d-akt">
          {!isEditing && <button className="vv-k" onClick={handleStartEdit}><Sym n="stift" /> Bearbeiten</button>}
          {istKonto && <button className="vv-k vv-k-haupt" onClick={handleOpenTurnierAnmeldung}><Sym n="pokal" /> Zum Turnier melden</button>}
          {istKonto && (
            <button className="vv-k vv-k-icon" aria-label="Weitere Aktionen" aria-haspopup="menu" aria-expanded={menue} onClick={(e) => { e.stopPropagation(); setMenue(!menue); }}><Sym n="mehr" /></button>
          )}
          {menue && (
            <div className="vv-menue" role="menu" onClick={(e) => e.stopPropagation()}>
              {gesperrt
                ? <button role="menuitem" onClick={() => oeffneDialog('freigeben')}><Sym n="offen" /> Zugang freigeben</button>
                : <button role="menuitem" onClick={() => oeffneDialog('sperren')}><Sym n="schloss" /> Zugang sperren</button>}
              <button role="menuitem" onClick={() => oeffneDialog('archiv')}><Sym n="archiv" /> Archivieren</button>
              <hr />
              <button role="menuitem" className="vv-gefahr" onClick={() => oeffneDialog('loeschen')}><Sym n="muell" /> Endgültig löschen</button>
            </div>
          )}
        </div>
      </div>

      {gesperrt && (
        <div className="vv-band vv-rot">
          <Sym n="schloss" />
          <div><b>Zugang gesperrt{verein.gesperrt_am ? ` seit ${datum(verein.gesperrt_am)}` : ''}</b>Grund: {verein.gesperrt_grund || 'ohne Angabe'}. Anmelden geht nicht mehr, gemeldete Starter bleiben bestehen.</div>
          <button className="vv-k vv-k-klein" onClick={() => oeffneDialog('freigeben')}><Sym n="offen" /> Freigeben</button>
        </div>
      )}

      <div className="vv-d-zahlen">
        <div className="vv-d-zahl"><small>Wettkämpfer</small><b>{anzahlWk}</b><span>{jugend} Jugend · {anzahlWk - jugend} Erwachsene</span></div>
        <div className="vv-d-zahl"><small>Geschlecht</small><b>{maennlich} / {weiblich}</b><span>männlich / weiblich</span></div>
        <div className="vv-d-zahl"><small>Meldungen</small><b>{aktiveMeldungen}</b><span>bei {turniereMitMeldung.filter((t) => t.starts > 0).length} Turnieren</span></div>
        <div className={`vv-d-zahl${ohneGew ? ' vv-warn' : ''}`}><small>Ohne Gewicht</small><b>{ohneGew}</b><span>{ohneGew ? 'für Gewichtsklassen nötig' : 'alles eingetragen'}</span></div>
        <div className="vv-d-zahl"><small>Letzter Login</small><b className="vv-b-datum">{verein.letzter_login ? datum(verein.letzter_login) : '–'}</b><span>{loginTage === null ? 'noch nie' : loginTage === 0 ? 'heute' : `vor ${loginTage} ${loginTage === 1 ? 'Tag' : 'Tagen'}`}</span></div>
      </div>

      <div className="vv-d-reiter" role="tablist">
        {reiter.map(([k, t, n]) => (
          <button key={k} role="tab" aria-selected={activeTab === k} onClick={() => wechsleReiter(k)}>{t}{n !== null && <em>{n}</em>}</button>
        ))}
      </div>
      <div className="vv-d-inhalt">{inhalt}</div>

      {dialog?.art === 'sperren' && <SperrenDialog verein={verein} onSchliessen={zu} onOk={async (grund) => { await aktion.sperren(verein, grund); await nachStatus('Zugang gesperrt'); }} />}
      {dialog?.art === 'freigeben' && <FreigebenDialog verein={verein} onSchliessen={zu} onOk={async () => { await aktion.freigeben(verein); await nachStatus('Zugang freigegeben. Der Verein kann sich wieder anmelden.'); }} />}
      {dialog?.art === 'archiv' && <ArchivDialog verein={verein} wettkaempfer={anzahlWk} meldungen={aktiveMeldungen} onSchliessen={zu}
        onOk={async (grund) => { await aktion.archivieren(verein, grund); setDialog(null); toast.ok('Verein archiviert. Er steht jetzt im Archiv.'); navigate('/dashboard/vereine'); }} />}
      {dialog?.art === 'loeschen' && <LoeschenDialog verein={verein} wettkaempfer={anzahlWk} meldungen={aktiveMeldungen} onSchliessen={zu}
        onArchivieren={() => setDialog({ art: 'archiv', daten: verein })}
        onOk={async (name) => { await aktion.loeschen(verein.vereins_id, name); setDialog(null); toast.ok(`${verein.name} endgültig gelöscht`); navigate('/dashboard/vereine'); }} />}
      {dialog?.art === 'wkLoeschen' && <BestaetigenDialog sym="muell" rot titel={`${dd.vorname} ${dd.nachname} löschen?`} text="Das lässt sich nicht rückgängig machen."
        punkte={[`${meldungenJeWk[dd.wettkaempfer_id] || 0} Turnier-Meldungen werden mit gelöscht.`, 'Foto und Einwilligung werden mit gelöscht.']} knopf="Löschen"
        onSchliessen={zu} onOk={() => wkLoeschen(dd)} />}
      {dialog?.art === 'storno' && <BestaetigenDialog sym="x" rot titel="Meldung stornieren?" text={`${dd.name} · ${dd.division_code || 'ohne Kategorie'}`}
        punkte={['Der Start fällt aus der Startliste.', 'Die Meldung bleibt als storniert sichtbar.']} knopf="Stornieren"
        onSchliessen={zu} onOk={() => stornieren(dd.anmeldung_id)} />}
      {dialog?.art === 'personal' && <BestaetigenDialog sym="muell" titel={`${dd.vorname} ${dd.nachname} deaktivieren?`} text="Die Person steht danach nicht mehr in der Einteilung."
        punkte={['Frühere Einsätze bleiben erhalten.']} knopf="Deaktivieren"
        onSchliessen={zu} onOk={() => personalDeaktivieren(dd)} />}
    </div>

      {/* Modal für neues Mitglied */}
      <Modal open={showAddModal} title="Wettkämpfer anlegen" onClose={() => setShowAddModal(false)}>
        <form onSubmit={handleWettkaempferSubmit} className="wettkaempfer-form">
          <div className="form-grid">
            <div className="form-column">
              <label>
                Vorname *
                <input
                  type="text"
                  value={newMember.vorname}
                  onChange={e => setNewMember({...newMember, vorname: e.target.value})}
                  required
                />
              </label>
              <label>
                Nachname *
                <input
                  type="text"
                  value={newMember.nachname}
                  onChange={e => setNewMember({...newMember, nachname: e.target.value})}
                  required
                />
              </label>
              <label>
                Geburtsdatum *
                <input
                  type="date"
                  value={newMember.geburtsdatum}
                  onChange={e => setNewMember({...newMember, geburtsdatum: e.target.value})}
                  required
                />
              </label>
              <label>
                Gewicht (kg)
                <input
                  type="number"
                  value={newMember.gewicht}
                  onChange={e => setNewMember({...newMember, gewicht: e.target.value})}
                  min="0"
                  step="0.1"
                />
              </label>
              <label>
                Skill Level *
                <select
                  value={newMember.skill_level}
                  onChange={e => setNewMember({...newMember, skill_level: e.target.value})}
                  required
                >
                  <option value="">Bitte wählen</option>
                  {skillLevels.map(level => <option key={level} value={level}>{level}</option>)}
                </select>
              </label>
              <label>
                Geschlecht *
                <select
                  value={newMember.geschlecht}
                  onChange={e => setNewMember({...newMember, geschlecht: e.target.value})}
                  required
                >
                  <option value="">Bitte wählen</option>
                  <option value="männlich">männlich</option>
                  <option value="weiblich">weiblich</option>
                </select>
              </label>
            </div>
            <div className="form-column">
              <label>
                Nationalität
                <input
                  type="text"
                  value={newMember.nationalitaet}
                  onChange={e => setNewMember({...newMember, nationalitaet: e.target.value})}
                />
              </label>
              <label>
                Gurtfarbe *
                <select
                  value={newMember.gurtfarbe}
                  onChange={e => setNewMember({...newMember, gurtfarbe: e.target.value, skill_level: skillAusGurt(e.target.value) || newMember.skill_level})}
                  required
                >
                  <option value="">Bitte wählen</option>
                  {gurtfarben.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </label>
              <label>
                Kampfstil
                <input
                  type="text"
                  value={newMember.kampfstil}
                  onChange={e => setNewMember({...newMember, kampfstil: e.target.value})}
                />
              </label>
              <label>
                E-Mail
                <input
                  type="email"
                  value={newMember.email}
                  onChange={e => setNewMember({...newMember, email: e.target.value})}
                />
              </label>
              <label>
                Handy
                <input
                  type="tel"
                  value={newMember.handy}
                  onChange={e => setNewMember({...newMember, handy: e.target.value})}
                />
              </label>
              <button className="submit-button" type="submit" disabled={saving}>
                {saving ? "Speichern..." : "Speichern"}
              </button>
              {modalError && <div className="error-message">{modalError}</div>}
            </div>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal open={showEditModal} title="Wettkämpfer bearbeiten" onClose={() => setShowEditModal(false)}>
        {editingWettkaempfer && (
          <form onSubmit={handleSaveEdit} className="wettkaempfer-form">
            {modalError && <div className="error-message">{modalError}</div>}
            <div className="form-grid">
              <div className="form-column">
                <label>
                  Vorname *
                  <input type="text" value={editingWettkaempfer.vorname} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, vorname: e.target.value})} required />
                </label>
                <label>
                  Nachname *
                  <input type="text" value={editingWettkaempfer.nachname} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, nachname: e.target.value})} required />
                </label>
                <label>
                  Geburtsdatum *
                  <input type="date" value={editingWettkaempfer.geburtsdatum} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, geburtsdatum: e.target.value})} required />
                </label>
                <label>
                  Geschlecht *
                  <select value={editingWettkaempfer.geschlecht || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, geschlecht: e.target.value})} required>
                    <option value="">Bitte wählen</option>
                    <option value="männlich">männlich</option>
                    <option value="weiblich">weiblich</option>
                  </select>
                </label>
                <label>
                  Gewicht (kg) *
                  <input type="number" step="0.1" value={editingWettkaempfer.gewicht || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, gewicht: e.target.value})} required />
                </label>
                <label>
                  Skill Level *
                  <select value={editingWettkaempfer.skill_level || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, skill_level: e.target.value})} required>
                    <option value="">Bitte wählen</option>
                    {skillLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="form-column">
                <label>
                  Gurtfarbe
                  <select value={editingWettkaempfer.gurtfarbe || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, gurtfarbe: e.target.value, skill_level: skillAusGurt(e.target.value) || editingWettkaempfer.skill_level})}>
                    <option value="">Bitte wählen</option>
                    {gurtfarben.map(farbe => (
                      <option key={farbe} value={farbe}>{farbe}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Kampfstil
                  <input type="text" value={editingWettkaempfer.kampfstil || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, kampfstil: e.target.value})} />
                </label>
                <label>
                  Nationalität
                  <input type="text" value={editingWettkaempfer.nationalitaet || 'DE'} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, nationalitaet: e.target.value})} />
                </label>
                <label>
                  E-Mail
                  <input type="email" value={editingWettkaempfer.email || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, email: e.target.value})} />
                </label>
                <label>
                  Handy
                  <input type="tel" value={editingWettkaempfer.handy || ''} onChange={e => setEditingWettkaempfer({...editingWettkaempfer, handy: e.target.value})} />
                </label>
              </div>
            </div>
            <button className="submit-button" type="submit" disabled={saving}>
              {saving ? "Speichere..." : "Änderungen speichern"}
            </button>
          </form>
        )}
      </Modal>

      {/* Detail Modal */}
      <Modal open={showDetailModal} title="Wettkämpfer" onClose={() => { setShowDetailModal(false); setFotoDatei(null); }}>
        {detailWettkaempfer && (
          <div className="detail-view">
            {/* Foto + Name */}
            <div className="detail-photo-area">
              <div className="detail-photo-wrapper">
                {/* Foto nur über die geprüfte Adresse, mit Anmeldung geholt (21.10.2026) */}
                <GeschuetztesBild
                  src={detailWettkaempfer.foto}
                  alt="Foto"
                  className="detail-photo"
                  ersatz={(
                    <div className="detail-photo-placeholder">
                      <span>👤</span>
                    </div>
                  )}
                />
                <label className="detail-photo-upload-btn" title="Foto hochladen">
                  📷
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFotoUpload}
                  />
                </label>
              </div>
              <div className="detail-name-area">
                <h2>{detailWettkaempfer.vorname} {detailWettkaempfer.nachname}</h2>
                {detailWettkaempfer.gurtfarbe && (
                  <span className="detail-gurt-badge">{detailWettkaempfer.gurtfarbe}</span>
                )}
              </div>
            </div>

            <FotoHallenEinwilligung
              wk={detailWettkaempfer}
              datei={fotoDatei}
              onAbbrechen={() => setFotoDatei(null)}
              onGespeichert={fotoAnzeigeGespeichert}
              onFehler={(m) => toast.fehler(m)}
            />

            {/* Info Grid */}
            <div className="detail-info-grid">
              <div className="detail-info-item">
                <span className="detail-info-label">Geburtsdatum</span>
                <span className="detail-info-value">{formatDate(detailWettkaempfer.geburtsdatum) || '–'}</span>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-label">Geschlecht</span>
                <span className="detail-info-value">{detailWettkaempfer.geschlecht || '–'}</span>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-label">Gewicht</span>
                <span className="detail-info-value">{detailWettkaempfer.gewicht ? `${detailWettkaempfer.gewicht} kg` : '–'}</span>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-label">Skill Level</span>
                <span className="detail-info-value">{detailWettkaempfer.skill_level || '–'}</span>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-label">Nationalität</span>
                <span className="detail-info-value">{detailWettkaempfer.nationalitaet || 'DE'}</span>
              </div>
              <div className="detail-info-item">
                <span className="detail-info-label">Kampfstil</span>
                <span className="detail-info-value">{detailWettkaempfer.kampfstil || '–'}</span>
              </div>
              {detailWettkaempfer.email && (
                <div className="detail-info-item detail-info-full">
                  <span className="detail-info-label">E-Mail</span>
                  <span className="detail-info-value">{detailWettkaempfer.email}</span>
                </div>
              )}
              {detailWettkaempfer.handy && (
                <div className="detail-info-item detail-info-full">
                  <span className="detail-info-label">Handy</span>
                  <span className="detail-info-value">{detailWettkaempfer.handy}</span>
                </div>
              )}
            </div>

            <div className="detail-modal-actions">
              <button className="detail-edit-btn" onClick={handleEditFromDetail}>
                Bearbeiten
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal für neues Personal */}
      <Modal open={showAddStaffModal} title="Personal anlegen" onClose={() => setShowAddStaffModal(false)}>
        <form onSubmit={handleStaffSubmit} className="wettkaempfer-form">
          <div className="form-grid">
            <div className="form-column">
              <label>
                Vorname *
                <input
                  type="text"
                  value={newStaff.vorname}
                  onChange={e => setNewStaff({...newStaff, vorname: e.target.value})}
                  required
                />
              </label>
              <label>
                Nachname *
                <input
                  type="text"
                  value={newStaff.nachname}
                  onChange={e => setNewStaff({...newStaff, nachname: e.target.value})}
                  required
                />
              </label>
              <label>
                Rolle *
                <select
                  value={newStaff.primaer_rolle}
                  onChange={e => setNewStaff({...newStaff, primaer_rolle: e.target.value})}
                  required
                >
                  {STAFF_ROLLEN.map(r => (
                    <option key={r.value} value={r.value}>{r.icon} {r.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Qualifikation
                <select
                  value={newStaff.qualifikation}
                  onChange={e => setNewStaff({...newStaff, qualifikation: e.target.value})}
                >
                  {QUALIFIKATIONEN.map(q => (
                    <option key={q.value} value={q.value}>{q.icon} {q.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="form-column">
              <label>
                E-Mail
                <input
                  type="email"
                  value={newStaff.email}
                  onChange={e => setNewStaff({...newStaff, email: e.target.value})}
                />
              </label>
              <label>
                Telefon
                <input
                  type="tel"
                  value={newStaff.telefon}
                  onChange={e => setNewStaff({...newStaff, telefon: e.target.value})}
                />
              </label>
              <label>
                Lizenz-Nr.
                <input
                  type="text"
                  value={newStaff.lizenz_nummer}
                  onChange={e => setNewStaff({...newStaff, lizenz_nummer: e.target.value})}
                  placeholder="z.B. DTU-12345"
                />
              </label>
              <label>
                Verband
                <input
                  type="text"
                  value={newStaff.verband}
                  onChange={e => setNewStaff({...newStaff, verband: e.target.value})}
                  placeholder="z.B. DTU, WTF"
                />
              </label>
              <button className="submit-button" type="submit" disabled={saving}>
                {saving ? "Speichern..." : "Speichern"}
              </button>
              {modalError && <div className="error-message">{modalError}</div>}
            </div>
          </div>
        </form>
      </Modal>

      {/* Turnier-Auswahl Modal */}
      <Modal open={showTurnierAuswahl} title="Turnier für Anmeldung wählen" onClose={() => setShowTurnierAuswahl(false)}>
        <div className="turnier-auswahl-content">
          {loadingTurniere ? (
            <div className="loading-container" style={{ padding: '2rem', textAlign: 'center' }}>
              <div className="loading-spinner"></div>
              <p>Turniere werden geladen...</p>
            </div>
          ) : verfuegbareTurniere.length === 0 ? (
            <div className="no-results" style={{ padding: '2rem', textAlign: 'center' }}>
              <h3>Keine verfügbaren Turniere</h3>
              <p>Derzeit sind keine Turniere mit offener Anmeldung verfügbar.</p>
            </div>
          ) : (
            <div className="turnier-auswahl-grid">
              {verfuegbareTurniere.map(turnier => (
                <div
                  key={turnier.turnier_id}
                  className="turnier-auswahl-card"
                  onClick={() => handleTurnierSelected(turnier)}
                >
                  <div className="turnier-auswahl-header">
                    <h3>{turnier.name}</h3>
                    <span className="turnier-datum">
                      {turnier.datum_formatted || formatDate(turnier.datum)}
                    </span>
                  </div>
                  <div className="turnier-auswahl-info">
                    <p>📍 {turnier.ort}</p>
                    <p>🥋 {turnier.disziplin || 'Alle Disziplinen'}</p>
                    {turnier.anmeldeschluss && (
                      <p className="anmeldeschluss">
                        ⏰ Anmeldeschluss: {turnier.anmeldeschluss_formatted || formatDate(turnier.anmeldeschluss)}
                      </p>
                    )}
                  </div>
                  <div className="turnier-auswahl-action">
                    <span>Wettkämpfer anmelden →</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Wettkämpfer-Auswahl Modal für Turnier-Anmeldung */}
      {showTurnierModal && selectedTurnierForAnmeldung && (
        <WettkampferAuswahlModal
          turnier={selectedTurnierForAnmeldung}
          onClose={() => {
            setShowTurnierModal(false);
            setSelectedTurnierForAnmeldung(null);
            setEditingAnmeldung(null);
          }}
          onAnmeldungAbgeschlossen={(data) => {
            handleAnmeldungAbgeschlossen(data);
            setEditingAnmeldung(null);
            fetchAnmeldungen();
          }}
          preselectedVerein={verein ? { vereins_id: verein.vereins_id, name: verein.name } : null}
          editingWettkaempfer={editingAnmeldung ? {
            wettkaempfer_id: editingAnmeldung.wettkaempfer_id,
            division_code: editingAnmeldung.division_code,
            existingDivisions: editingAnmeldung.existingDivisions || []
          } : null}
        />
      )}

    </>
  );
}
